import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Reset password — reached via emailed one-time link `/reset-password?token=…`.
// Visual mirrors the Login screen so nothing feels out of place.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const nav = useNavigate();

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [tokenError, setTokenError] = useState("");

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError("Ссылка неполная или повреждена. Запросите новую ссылку.");
        setChecking(false);
        return;
      }
      try {
        const { data } = await api.get(`/auth/reset-token/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setTokenValid(true);
        setMaskedEmail(data?.email || "");
      } catch (err) {
        if (cancelled) return;
        setTokenValid(false);
        setTokenError(formatApiError(err) || "Ссылка недействительна или уже использована.");
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) {
      toast.error("Пароль должен содержать минимум 6 символов");
      return;
    }
    if (pw1 !== pw2) {
      toast.error("Пароли не совпадают");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setSuccess(true);
      toast.success("Пароль обновлён");
      setTimeout(() => nav("/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(formatApiError(err) || "Не удалось обновить пароль");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="reset-password-page">
      <div
        className="flex-1 flex items-end"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(74,59,50,0.10) 0%, rgba(74,59,50,0.55) 100%), url('https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/pntpjlxa_79B8E46A-C20C-47B8-AD98-722E2F90A324.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="w-full bg-white rounded-t-[32px] px-7 pt-9 pb-10 shadow-[0_-12px_40px_rgba(0,0,0,0.15)]">
          <div className="mb-7">
            <div className="chip bg-sand/60 text-espresso inline-block mb-3">Anokina Studio</div>
            <h1 className="font-serif text-4xl leading-tight text-espresso" data-testid="reset-title">
              Новый<br />пароль
            </h1>
            {tokenValid && maskedEmail && (
              <p className="text-sm text-muted-foreground mt-2" data-testid="reset-masked-email">
                Восстановление для <span className="text-espresso">{maskedEmail}</span>
              </p>
            )}
          </div>

          {checking && (
            <div className="text-sm text-muted-foreground" data-testid="reset-checking">
              Проверяем ссылку…
            </div>
          )}

          {!checking && !tokenValid && (
            <div className="space-y-5" data-testid="reset-invalid-state">
              <div className="rounded-2xl bg-red-50 border border-red-200 text-red-800 px-4 py-4 text-sm leading-relaxed">
                {tokenError || "Ссылка недействительна или уже использована."}
              </div>
              <Link
                to="/forgot-password"
                data-testid="reset-request-new-link"
                className="btn-pill w-full h-12 bg-espresso hover:bg-espressoDark text-white text-base inline-flex items-center justify-center"
              >
                Запросить новую ссылку
              </Link>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-xs uppercase tracking-widest text-espresso/70 hover:text-espresso underline underline-offset-4"
                >
                  Вернуться ко входу
                </Link>
              </div>
            </div>
          )}

          {!checking && tokenValid && !success && (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Новый пароль</label>
                <Input
                  data-testid="reset-password-input"
                  type="password"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  required
                  minLength={6}
                  className="mt-1 h-12 rounded-2xl"
                  placeholder="••••••"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Повторите пароль</label>
                <Input
                  data-testid="reset-password-confirm-input"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  minLength={6}
                  className="mt-1 h-12 rounded-2xl"
                  placeholder="••••••"
                />
              </div>
              <Button
                data-testid="reset-submit-button"
                type="submit"
                disabled={submitting}
                className="btn-pill w-full h-12 bg-espresso hover:bg-espressoDark text-white text-base"
              >
                {submitting ? "Сохраняем…" : "Сохранить новый пароль"}
              </Button>
            </form>
          )}

          {!checking && tokenValid && success && (
            <div className="space-y-5" data-testid="reset-success-state">
              <div className="rounded-2xl bg-sand/40 border border-sand text-espresso px-4 py-4 text-sm leading-relaxed">
                Пароль обновлён. Сейчас перенаправим на страницу входа.
              </div>
              <Link
                to="/login"
                data-testid="reset-goto-login"
                className="btn-pill w-full h-12 bg-espresso hover:bg-espressoDark text-white text-base inline-flex items-center justify-center"
              >
                Войти сейчас
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
